import { Fragment } from "react"

import { getImageProps } from "next/image"

import { HeroReel } from "@/components/marketplace/hero/hero-reel"
import { Heading, LinkButton, Text } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"

// A two-zone hero: a solid brand-blue panel carrying the copy, welded to one
// photograph inside a single rounded frame. Stacked on phones — copy above the
// picture — and split side by side from @stage, copy left and picture right at a
// capped landscape height. The headline is one claim in two tones: a muted
// opening line and a solid-white payoff, set in the display face.
//

export interface HeroSlide {
  title: string
  accent?: string
  sub?: string
  cta?: string
  ctaHref?: string
  image?: string
  imageWide?: string
  imageAlt?: string
  imageCrop?: ImageCrop
  serveId?: string
}

const WORD_STAGGER_MS = 55

// The photo panel is portrait/near-square beside the copy from @stage and a
// landscape strip above it on phones, so the crops invert the usual: the portrait
// `tall` art fills the desktop panel, the `wide` art the mobile strip — a wide
// crop in the square desktop panel upscales and over-crops, and a portrait crop in
// the mobile strip over-crops the other way. A <picture> media split fetches
// exactly one (1008px ≈ the 63rem @stage seam); display:none would download both.
const HeroArtDirected = ({ tall, wide, alt, priority }: { tall: string; wide: string; alt: string; priority: boolean }) => {
  const { props: desktopProps } = getImageProps({ src: tall, alt, fill: true, sizes: "48vw" })
  const { props: mobileProps } = getImageProps({ src: wide, alt, fill: true, sizes: "100vw", loading: priority ? "eager" : "lazy" })
  return (
    <picture>
      <source media="(min-width: 1008px)" srcSet={desktopProps.srcSet} sizes={desktopProps.sizes} />
      <img {...mobileProps} alt={alt} fetchPriority={priority ? "high" : undefined} className="absolute inset-0 size-full object-cover" />
    </picture>
  )
}

// Each word is its own inline-block so the rise-in transform has a box to move.
// The inter-word space must live BETWEEN the spans as its own text node — a space
// placed inside an inline-block is trailing whitespace and the browser strips it,
// which welds the words together ("Estrenaeldíadehoy").
const Words = ({ text, from = 0, className }: { text: string; from?: number; className?: string }) => (
  <>
    {text.split(" ").map((word, index) => (
      <Fragment key={`${word}-${index}`}>
        {index > 0 ? " " : null}
        <span className={`motion-safe:animate-rise-in inline-block ${className ?? ""}`} style={{ animationDelay: `${(from + index) * WORD_STAGGER_MS}ms` }}>
          {word}
        </span>
      </Fragment>
    ))}
  </>
)

const HeroCopy = ({ slide }: { slide: HeroSlide }) => (
  <div className="flex flex-col items-center gap-250 text-center @2xl:items-start @2xl:gap-400 @2xl:text-left">
    {/* Two lines, one claim: the opening line recedes so the payoff carries. The
        accent is the bright line here — on the blue panel the emphasis is weight
        of white, not a second hue. */}
    <Heading as="h1" variant="heroSplit" family="hero" tone="inverted" balance>
      <span className="text-fg-inverted/85 block">
        <Words text={slide.title} />
      </span>{" "}
      {slide.accent && (
        <span className="block">
          <Words text={slide.accent} from={slide.title.split(" ").length} />
        </span>
      )}
    </Heading>
    {slide.sub && (
      <Text as="p" variant="body" family="text" tone="inverted" className="motion-safe:animate-rise-in animation-delay-200 @stage:text-body-lg max-w-90 font-normal">
        {slide.sub}
      </Text>
    )}
    {slide.cta && (
      <div className="motion-safe:animate-rise-in animation-delay-300 mt-250 flex w-full @2xl:mt-350 @2xl:w-auto">
        <LinkButton href={slide.ctaHref} variant="inverse" fullWidth className="pointer-events-auto font-normal @2xl:w-auto">
          {slide.cta}
        </LinkButton>
      </div>
    )}
  </div>
)

const HeroCarouselFrame = ({ slide, priority }: { slide: HeroSlide; priority: boolean }) => {
  const tall = slide.image ?? slide.imageWide
  const wide = slide.imageWide ?? slide.image

  return (
    <div data-nelo-serve={slide.serveId} className="press-banner rounded-12x @2xl:h-hero-stage @2xl:grid-cols-hero-stage relative grid overflow-hidden @2xl:grid-rows-1">
      <CoverLink href={slide.cta ? slide.ctaHref : undefined} />
      <div className="bg-brand @stage:px-1250 pointer-events-none relative flex min-h-0 min-w-0 flex-col justify-center px-500 py-500 @2xl:px-700 @2xl:py-0">
        <HeroCopy slide={slide} />
      </div>
      <div className="bg-surface-muted aspect-hero-photo-sm pointer-events-none relative isolate min-h-0 min-w-0 overflow-hidden @2xl:aspect-auto">
        {tall && tall === wide && (
          <TileImage src={tall} crop={slide.imageCrop} alt={slide.imageAlt ?? ""} fill sizes="(min-width: 1008px) 48vw, 100vw" priority={priority} className="object-cover" />
        )}
        {tall && wide && tall !== wide && <HeroArtDirected tall={tall} wide={wide} alt={slide.imageAlt ?? ""} priority={priority} />}
      </div>
    </div>
  )
}

export const HeroCarousel = ({ slides, priority = false }: { slides: HeroSlide[]; priority?: boolean }) => {
  if (slides.length === 0) return null

  const frames = slides.map((slide, index) => <HeroCarouselFrame key={index} slide={slide} priority={priority && index === 0} />)

  return (
    <div className="@container">
      <section className="mx-gut">{frames.length === 1 ? frames : <HeroReel>{frames}</HeroReel>}</section>
    </div>
  )
}
